import React, { forwardRef } from "react";

import type Konva from "konva";
import { Transformer } from "react-konva";

import { MarkupMode } from "../types";

interface AnnotationTransformerProps {
  selectedId: string | null;
  isAnnotating: boolean;
  markupMode: MarkupMode;
  rectX: number;
  rectY: number;
  rectWidth: number;
  rectHeight: number;
}

/**
 * AnnotationTransformer component
 * Preserves exact transformer configuration from original implementation
 */
export const AnnotationTransformer = forwardRef<Konva.Transformer, AnnotationTransformerProps>(
  ({ selectedId, isAnnotating, markupMode, rectX, rectY, rectWidth, rectHeight }, ref) => {
    if (!selectedId || !isAnnotating || markupMode !== MarkupMode.Selection) {
      return null;
    }

    return (
      <Transformer
        anchorCornerRadius={2}
        anchorFill="white"
        anchorSize={8}
        anchorStroke="#F27D28"
        anchorStrokeWidth={1}
        borderStroke="#F27D28"
        borderStrokeWidth={3.5}
        boundBoxFunc={(oldBox, newBox) => {
          // Limit minimum size
          if (newBox.width < 10 || newBox.height < 10) {
            return oldBox;
          }

          // Limit to image boundaries
          const maxX = rectX + rectWidth;
          const maxY = rectY + rectHeight;

          if (newBox.x < rectX) newBox.x = rectX;
          if (newBox.y < rectY) newBox.y = rectY;
          if (newBox.x + newBox.width > maxX) {
            newBox.width = maxX - newBox.x;
          }
          if (newBox.y + newBox.height > maxY) {
            newBox.height = maxY - newBox.y;
          }

          return newBox;
        }}
        enabledAnchors={["top-left", "top-right", "bottom-left", "bottom-right"]}
        ignoreStroke={true}
        keepRatio={false}
        ref={ref}
        rotateEnabled={false}
      />
    );
  },
);

AnnotationTransformer.displayName = "AnnotationTransformer";
